export type PtBrPdfCategory =
  | "Organizar e Editar"
  | "Otimizar e Reparar"
  | "Converter para PDF"
  | "Converter de PDF"
  | "Segurança e Utilitários";

export interface PtBrPdfToolMeta {
  slug: string;
  enSlug: string;
  name: string;
  tagline: string;
  description: string;
  category: PtBrPdfCategory;
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const PT_BR_PDF_CATEGORIES: PtBrPdfCategory[] = [
  "Organizar e Editar",
  "Otimizar e Reparar",
  "Converter para PDF",
  "Converter de PDF",
  "Segurança e Utilitários",
];

export const PT_BR_PDF_TOOLS: PtBrPdfToolMeta[] = [
  // Organizar e Editar
  {
    slug: "juntar-pdf",
    enSlug: "merge-pdf",
    name: "Juntar PDF Grátis Online (Combinar PDFs)",
    tagline: "Junte vários arquivos PDF em um único documento organizado",
    description: "Combine dois ou mais documentos PDF em um único arquivo de forma rápida e 100% privada no seu navegador.",
    category: "Organizar e Editar",
    icon: "FilePlus",
    featured: true,
    keywords: ["juntar pdf", "combinar pdf", "unir pdf gratis", "juntar arquivos pdf online"],
  },
  {
    slug: "dividir-pdf",
    enSlug: "split-pdf",
    name: "Dividir PDF Online Grátis",
    tagline: "Separe páginas ou extraia intervalos de um documento PDF",
    description: "Extraia páginas específicas ou divida um PDF grande em vários arquivos menores sem instalar programas.",
    category: "Organizar e Editar",
    icon: "Scissors",
    featured: true,
    keywords: ["dividir pdf", "separar paginas pdf", "extrair paginas pdf online"],
  },
  {
    slug: "organizar-pdf",
    enSlug: "organize-pdf",
    name: "Organizar Páginas de PDF",
    tagline: "Reordene, exclua e gire páginas de PDF arrastando e soltando",
    description: "Altere a ordem das páginas do seu PDF, remova folhas indesejadas e organize seu documento visualmente.",
    category: "Organizar e Editar",
    icon: "LayoutGrid",
    featured: true,
    keywords: ["organizar pdf", "reordenar paginas pdf", "excluir paginas pdf"],
  },
  {
    slug: "girar-pdf",
    enSlug: "rotate-pdf",
    name: "Girar PDF Online Grátis",
    tagline: "Gire páginas individuais ou todas as folhas do PDF permanentemente",
    description: "Corrija páginas de ponta-cabeça ou de lado girando 90, 180 ou 270 graus direto no seu navegador.",
    category: "Organizar e Editar",
    icon: "RotateCw",
    keywords: ["girar pdf", "rodar pdf online", "virar pagina pdf"],
  },
  {
    slug: "extrair-paginas-pdf",
    enSlug: "extract-pages",
    name: "Extrair Páginas de PDF",
    tagline: "Salve apenas as páginas selecionadas em um novo PDF",
    description: "Selecione as páginas exatas que você precisa e gere um novo documento leve em segundos.",
    category: "Organizar e Editar",
    icon: "FileOutput",
    keywords: ["extrair paginas pdf", "salvar paginas especificas pdf"],
  },
  {
    slug: "excluir-paginas-pdf",
    enSlug: "remove-pages",
    name: "Excluir Páginas de PDF",
    tagline: "Remova folhas em branco ou desnecessárias do seu PDF",
    description: "Exclua páginas indesejadas de um PDF de forma definitiva sem fazer upload do arquivo para a nuvem.",
    category: "Organizar e Editar",
    icon: "Trash2",
    keywords: ["excluir paginas pdf", "remover paginas pdf", "deletar folha pdf"],
  },
  {
    slug: "editar-pdf-online",
    enSlug: "edit-pdf",
    name: "Editor de PDF Online Grátis",
    tagline: "Adicione texto, assinaturas, formas e imagens em arquivos PDF",
    description: "Edite PDFs grátis adicionando anotações, campos de texto, assinaturas desenhadas e imagens sobre o documento.",
    category: "Organizar e Editar",
    icon: "Edit3",
    featured: true,
    keywords: ["editar pdf online", "editor de pdf gratis", "escrever em pdf online"],
  },
  {
    slug: "numerar-paginas-pdf",
    enSlug: "page-numbers",
    name: "Numerar Páginas de PDF",
    tagline: "Insira números de página no cabeçalho ou rodapé do PDF",
    description: "Adicione numeração automática em seus trabalhos e relatórios com formatação e alinhamento personalizáveis.",
    category: "Organizar e Editar",
    icon: "Hash",
    keywords: ["numerar paginas pdf", "adicionar numero de pagina pdf"],
  },
  {
    slug: "marca-dagua-pdf",
    enSlug: "watermark-pdf",
    name: "Adicionar Marca d'Água no PDF",
    tagline: "Insira marcas d'água de texto ou imagem nos seus PDFs",
    description: "Proteja seus documentos adicionando marcas d'água com texto personalizado ou logotipo da empresa.",
    category: "Organizar e Editar",
    icon: "Stamp",
    keywords: ["marca dagua pdf", "adicionar marca dagua pdf", "proteger documento marca dagua"],
  },

  // Otimizar e Reparar
  {
    slug: "compactar-pdf",
    enSlug: "compress-pdf",
    name: "Compactar PDF Grátis Online (Reduzir Tamanho)",
    tagline: "Reduza o peso do PDF para envio por e-mail mantendo a qualidade",
    description: "Comprima arquivos PDF pesados para enviar como anexo mantendo excelente nitidez do texto e imagens.",
    category: "Otimizar e Reparar",
    icon: "Minimize2",
    featured: true,
    keywords: ["compactar pdf", "reduzir tamanho pdf", "diminuir tamanho pdf gratis"],
  },
  {
    slug: "converter-pdf-em-preto-e-branco",
    enSlug: "grayscale-pdf",
    name: "Convert PDF em Preto e Branco (Escala de Cinza)",
    tagline: "Converta PDFs coloridos em escala de cinza para economizar tinta",
    description: "Transforme documentos coloridos em preto e branco para economizar tinta ao imprimir.",
    category: "Otimizar e Reparar",
    icon: "EyeOff",
    keywords: ["pdf preto e branco", "converter pdf escala de cinza", "economizar tinta pdf"],
  },
  {
    slug: "reparar-pdf",
    enSlug: "repair-pdf",
    name: "Reparar PDF Corrompido",
    tagline: "Recupere dados e textos de arquivos PDF danificados",
    description: "Repare arquivos PDF com erros de leitura ou corrompidos diretamente no seu navegador.",
    category: "Otimizar e Reparar",
    icon: "Wrench",
    keywords: ["reparar pdf", "recuperar pdf corrompido", "corrigir erro pdf"],
  },

  // Converter para PDF
  {
    slug: "converter-jpg-em-pdf",
    enSlug: "image-to-pdf",
    name: "Converter Imagem em PDF (JPG para PDF)",
    tagline: "Transforme imagens JPG, PNG e WEBP em um único arquivo PDF",
    description: "Converta fotos JPG, PNG, WEBP e GIF em um PDF bem formatado. Ajuste margens e orientação em segundos.",
    category: "Converter para PDF",
    icon: "Image",
    featured: true,
    keywords: ["converter jpg em pdf", "transformar foto em pdf", "imagem para pdf gratis"],
  },
  {
    slug: "converter-word-em-pdf",
    enSlug: "word-to-pdf",
    name: "Converter Word em PDF (DOCX para PDF)",
    tagline: "Transforme documentos Word (.docx) em PDF profissional",
    description: "Converta arquivos do Microsoft Word em PDF mantendo a formatação e fontes originais intactas.",
    category: "Converter para PDF",
    icon: "FileText",
    featured: true,
    keywords: ["converter word em pdf", "doc para pdf", "docx em pdf online"],
  },
  {
    slug: "converter-excel-em-pdf",
    enSlug: "excel-to-pdf",
    name: "Converter Excel em PDF (XLSX para PDF)",
    tagline: "Converta planilhas de Excel em tabelas PDF prontas para impressão",
    description: "Transforme suas planilhas .xlsx em documentos PDF claros e bem formatados.",
    category: "Converter para PDF",
    icon: "Table",
    keywords: ["converter excel em pdf", "xlsx para pdf", "planilha em pdf"],
  },
  {
    slug: "converter-powerpoint-em-pdf",
    enSlug: "ppt-to-pdf",
    name: "Converter PowerPoint em PDF (PPTX para PDF)",
    tagline: "Transforme apresentações de slides PPTX em um PDF leve",
    description: "Converta slides de apresentações do PowerPoint em arquivos PDF compactos para compartilhar.",
    category: "Converter para PDF",
    icon: "Presentation",
    keywords: ["converter powerpoint em pdf", "pptx para pdf", "slide em pdf"],
  },
  {
    slug: "converter-html-em-pdf",
    enSlug: "html-to-pdf",
    name: "Converter HTML em PDF",
    tagline: "Transforme códigos ou páginas web HTML em PDF formated",
    description: "Salve código HTML ou textos da web em um PDF limpo e fácil de ler.",
    category: "Converter para PDF",
    icon: "Code",
    keywords: ["converter html em pdf", "salvar pagina web pdf"],
  },
  {
    slug: "converter-texto-em-pdf",
    enSlug: "txt-to-pdf",
    name: "Converter Texto (TXT) em PDF",
    tagline: "Transforme arquivos de texto simples (.txt) em documentos PDF",
    description: "Converta notas e arquivos .txt em PDFs legíveis e bem diagramados.",
    category: "Converter para PDF",
    icon: "FileCode",
    keywords: ["converter txt em pdf", "texto simples para pdf"],
  },

  // Converter desde PDF
  {
    slug: "converter-pdf-em-jpg",
    enSlug: "pdf-to-image",
    name: "Converter PDF em JPG / Imagens",
    tagline: "Extraia cada página do PDF como imagem JPG ou PNG de alta qualidade",
    description: "Converta páginas de arquivos PDF em imagens JPG ou PNG em alta resolução instantaneamente.",
    category: "Converter desde PDF",
    icon: "FileImage",
    featured: true,
    keywords: ["converter pdf em jpg", "pdf para imagem", "extrair imagens do pdf"],
  },
  {
    slug: "converter-pdf-em-word",
    enSlug: "pdf-to-word",
    name: "Converter PDF em Word (PDF para DOCX)",
    tagline: "Extraia o conteúdo do PDF em um documento Word editável",
    description: "Converta documentos PDF em arquivos do Microsoft Word (.docx) para editar o texto facilmente.",
    category: "Converter desde PDF",
    icon: "FileSpreadsheet",
    featured: true,
    keywords: ["converter pdf em word", "pdf para docx editavel", "transformar pdf em word"],
  },
  {
    slug: "converter-pdf-em-excel",
    enSlug: "pdf-to-excel",
    name: "Converter PDF em Excel (PDF para XLSX)",
    tagline: "Extraia tabelas de dados do PDF para planilhas do Excel",
    description: "Extraia tabelas contidas em PDFs e converta em planilhas editáveis do Excel.",
    category: "Converter desde PDF",
    icon: "TableProperties",
    keywords: ["converter pdf em excel", "pdf para xlsx", "extrair tabela pdf excel"],
  },
  {
    slug: "extrair-texto-do-pdf",
    enSlug: "pdf-to-text",
    name: "Extrair Texto do PDF (PDF para TXT)",
    tagline: "Copie e extraia todo o texto escrito de um arquivo PDF",
    description: "Obtenha todo o conteúdo de texto de um PDF em um arquivo .txt leve sem formatações complexas.",
    category: "Converter desde PDF",
    icon: "FileLines",
    keywords: ["extrair texto do pdf", "pdf para txt", "copiar texto de pdf"],
  },

  // Segurança e Utilitários
  {
    slug: "proteger-pdf-com-senha",
    enSlug: "protect-pdf",
    name: "Proteger PDF com Senha",
    tagline: "Criptografe e bloqueie seu PDF com senha de segurança",
    description: "Proteja documentos PDF confidenciais aplicando senha de abertura forte com criptografia local.",
    category: "Segurança e Utilitários",
    icon: "Lock",
    featured: true,
    keywords: ["proteger pdf com senha", "colocar senha no pdf", "criptografar pdf"],
  },
  {
    slug: "desbloquear-pdf",
    enSlug: "unlock-pdf",
    name: "Desbloquear PDF (Remover Senha)",
    tagline: "Remova a senha e restrições de edição de arquivos PDF próprios",
    description: "Remova senhas de abertura e restrições de cópia de arquivos PDF dos quais você seja o proprietário.",
    category: "Segurança e Utilitários",
    icon: "Unlock",
    keywords: ["desbloquear pdf", "remover senha do pdf", "tirar senha pdf online"],
  },
  {
    slug: "assinar-pdf-online",
    enSlug: "sign-pdf",
    name: "Assinar PDF Online Grátis",
    tagline: "Desenhe ou envie sua assinatura digital para assinar documentos",
    description: "Assine contratos e formulários PDF desenhando sua assinatura na tela ou enviando uma imagem da sua rubrica.",
    category: "Segurança e Utilitários",
    icon: "PenTool",
    featured: true,
    keywords: ["assinar pdf online", "assinatura digital pdf", "assinar documento pdf gratis"],
  },
  {
    slug: "ocr-em-pdf-reconhecimento-de-texto",
    enSlug: "ocr-pdf",
    name: "OCR em PDF (Reconhecimento de Texto)",
    tagline: "Reconheça textos em PDFs escaneados e torne-os pesquisáveis",
    description: "Utilize reconhecimento óptico de caracteres (OCR) para transformar documentos escaneados em texto pesquisável.",
    category: "Segurança e Utilitários",
    icon: "ScanText",
    keywords: ["ocr pdf gratis", "reconhecimento de texto pdf", "pdf escaneado para texto"],
  },
  {
    slug: "gerador-de-codigo-qr",
    enSlug: "qr-code",
    name: "Gerador de Código QR",
    tagline: "Crie códigos QR personalizados para links, Wi-Fi, vCards e textos",
    description: "Gere códigos QR estilizados para sites, redes Wi-Fi, cartões de contato e textos. Baixe em PNG ou carimbe diretamente em páginas PDF.",
    category: "Segurança e Utilitários",
    icon: "QrCode",
    featured: true,
    keywords: ["gerador de codigo qr", "criar codigo qr gratis", "codigo qr para wifi", "codigo qr link"],
  },
];

export function getPtBrPdfToolBySlug(slug: string): PtBrPdfToolMeta | undefined {
  return PT_BR_PDF_TOOLS.find((t) => t.slug === slug || t.enSlug === slug);
}

export function getEnPdfSlugForPtBrSlug(ptBrSlug: string): string {
  const found = PT_BR_PDF_TOOLS.find((t) => t.slug === ptBrSlug || t.enSlug === ptBrSlug);
  return found ? found.enSlug : ptBrSlug;
}

export function getPtBrPdfSlugForEnSlug(enSlug: string): string {
  const found = PT_BR_PDF_TOOLS.find((t) => t.enSlug === enSlug || t.slug === enSlug);
  return found ? found.slug : enSlug;
}

export function relatedPtBrPdfTools(currentSlug: string, count = 4): PtBrPdfToolMeta[] {
  const current = getPtBrPdfToolBySlug(currentSlug);
  return PT_BR_PDF_TOOLS.filter((t) => t.slug !== current?.slug).slice(0, count);
}
